// Honeypot DOM injection for ft.js. Picked out as a lib helper so it can
// be unit-tested under jsdom (see tests/script/honeypot.test.ts).
//
// Strategy: every form gets one randomly-named hidden input. Bots that
// blindly fill every input by name leak a value into a key that humans
// never see. The server-side detector at lib/ingest/honeypot.ts flags
// any non-empty `hp_*` field as `is_spam=true` (soft signal — we never
// reject the capture).

export function randomSuffix() {
  let s = "";
  const alphabet = "abcdefghijklmnopqrstuvwxyz0123456789";
  for (let i = 0; i < 6; i++) {
    s += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
  }
  return s;
}

// Inject a honeypot field into a single form. Idempotent — bails if
// the form already carries an `hp_*` input. Wrapped in try/catch so
// hostile DOMs (e.g. closed shadow roots) never throw out of the
// tracking script.
export function injectHoneypot(form) {
  try {
    if (!form || form.nodeType !== 1) return;
    if (form.querySelector('[name^="hp_"]')) return; // already injected
    const input = form.ownerDocument.createElement("input");
    input.type = "text";
    input.name = "hp_" + randomSuffix();
    input.value = "";
    input.tabIndex = -1;
    input.autocomplete = "off";
    input.setAttribute("aria-hidden", "true");
    // Visually invisible but still in the DOM. Screen readers skip it
    // (aria-hidden), keyboards skip it (tabindex=-1), pointer events
    // disabled. Bots that scrape input[name] still find it.
    input.style.cssText =
      "opacity:0;position:absolute;left:-9999px;top:auto;width:1px;height:1px;overflow:hidden;pointer-events:none";
    form.appendChild(input);
  } catch (_e) {
    // Never throw from a tracking script.
  }
}

// Inject honeypots into every form under the given root (Document or Element).
export function injectHoneypotsInto(root) {
  try {
    const forms = (root || document).querySelectorAll("form");
    for (let i = 0; i < forms.length; i++) injectHoneypot(forms[i]);
  } catch (_e) {
    // Swallow — root may not support querySelectorAll (e.g. closed shadow root).
  }
}
